import {
    type ChatMessage as ChatMessageType,
    formatUserMessageForDisplay,
    getAssistantDisplayContent,
    getLatestAssistantMessageLogoIconTarget,
    isAssistantTurnAwaitingWidget,
    isAssistantTurnWaitingForFirstToken,
    type SendSilentMessageOptions,
    type SproutStreamModelId,
    type SproutStreamReasoningEffort,
} from '@fruitful/llm-service-shared';
import React, { useMemo, useRef } from 'react';
import { type ScrollView } from 'react-native';
import { LabelRegular, Spinner, View, YStack } from 'smoothie';

import type { AssistantContentPartRenderedEvent } from '../hooks/useAiChatRenderAnalytics';
import { useTurnAnchoredScroll } from '../hooks/useTurnAnchoredScroll';
import {
    CHAT_INPUT_DEFAULT_PLACEHOLDER,
    ChatInput,
} from './shared-chat/ChatInput';
import { ChatMessageLayout } from './shared-chat/ChatMessageLayout';
import { type ChatWidgetHandlers } from './shared-chat/ChatWidgetRenderer';
import { SequentialAssistantContent } from './shared-chat/SequentialAssistantContent';
import { SproutEmptyState } from './shared-chat/SproutEmptyState';
import { SproutStreamSelectionHint } from './shared-chat/SproutOptionsButton';
import { TypingIndicator } from './shared-chat/TypingIndicator';
import { UserMessageBubble } from './shared-chat/UserMessageBubble';

interface ChatContainerProps {
    messages: ChatMessageType[];
    isStreaming: boolean;
    showTypingIndicator?: boolean;
    /** When true, suppress empty-state splash while a prior thread is loading */
    isRestoringHistory?: boolean;
    disabled?: boolean;
    placeholder?: string;
    emptyStateMessage?: string;
    greeting?: string;
    suggestions?: string[];
    introCopy?: readonly string[];
    aiDisclosure?: string;
    sproutStreamModelId?: SproutStreamModelId;
    sproutStreamReasoningEffort?: SproutStreamReasoningEffort;
    /** Shown in the dev-tools hint when this chat is not on Sprout. */
    agentKey?: string;
    onSendMessage: (message: string) => void;
    onSendMessageWithAgentPayload: (
        displayText: string,
        agentPayload: string,
    ) => Promise<void>;
    onSendSilentMessage: (
        message: string,
        options?: SendSilentMessageOptions,
    ) => Promise<void>;
    onStopStreaming?: () => void;
    onContentPartRendered?: (event: AssistantContentPartRenderedEvent) => void;
}

const getMessageText = (message: ChatMessageType): string =>
    message.content
        .filter((content) => content.type === 'text')
        .map((content) => content.text)
        .join('');

/**
 * Sprout chat container — scroll, empty state, messages, and shared input.
 */
export const ChatContainer = ({
    messages,
    isStreaming,
    showTypingIndicator = false,
    isRestoringHistory = false,
    disabled = false,
    placeholder = CHAT_INPUT_DEFAULT_PLACEHOLDER,
    emptyStateMessage,
    greeting,
    suggestions = [],
    introCopy,
    aiDisclosure,
    sproutStreamModelId,
    sproutStreamReasoningEffort,
    agentKey,
    onSendMessage,
    onSendMessageWithAgentPayload,
    onSendSilentMessage,
    onStopStreaming,
    onContentPartRendered,
}: ChatContainerProps) => {
    const scrollViewRef = useRef<ScrollView>(null);
    const {
        bottomPadding,
        onScrollViewLayout,
        notifyContentHeight,
        createAnchorOnLayout,
    } = useTurnAnchoredScroll({ scrollViewRef });

    const widgetHandlers = useMemo<ChatWidgetHandlers>(
        () => ({
            onSendSilentMessage,
            onSendMessageWithAgentPayload,
        }),
        [onSendSilentMessage, onSendMessageWithAgentPayload],
    );

    const lastMessage = messages[messages.length - 1];
    const isAwaitingWidget =
        lastMessage?.role === 'assistant' &&
        isAssistantTurnAwaitingWidget(lastMessage);
    const isWaitingForFirstToken =
        lastMessage?.role === 'assistant' &&
        isAssistantTurnWaitingForFirstToken(lastMessage);

    const isTypingIndicatorActive =
        (isStreaming && messages.length > 0) ||
        (showTypingIndicator && !isStreaming && messages.length > 0) ||
        isAwaitingWidget ||
        isWaitingForFirstToken;

    const logoIconTarget = isTypingIndicatorActive
        ? null
        : getLatestAssistantMessageLogoIconTarget(messages, isStreaming);

    const lastUserMessageId = [...messages]
        .reverse()
        .find((m) => m.role === 'user')?.id;

    return (
        <ChatMessageLayout
            scrollViewRef={scrollViewRef}
            onScrollViewLayout={onScrollViewLayout}
            footer={
                <YStack width="100%" alignItems="center">
                    <ChatInput
                        onSend={onSendMessage}
                        onStop={onStopStreaming}
                        isStreaming={isStreaming}
                        disabled={disabled}
                        placeholder={placeholder}
                        compactBottom
                    />
                    {sproutStreamModelId != null &&
                    sproutStreamReasoningEffort != null ? (
                        <SproutStreamSelectionHint
                            modelId={sproutStreamModelId}
                            reasoningEffort={sproutStreamReasoningEffort}
                            agentKey={agentKey}
                        />
                    ) : null}
                    {aiDisclosure != null ? (
                        <LabelRegular
                            color="$black"
                            textAlign="center"
                            paddingTop="$3"
                            paddingBottom="$3"
                        >
                            {aiDisclosure}
                        </LabelRegular>
                    ) : null}
                </YStack>
            }
        >
            {isRestoringHistory && messages.length === 0 ? (
                <YStack width="100%" alignItems="center" paddingVertical="$6">
                    <Spinner />
                </YStack>
            ) : messages.length === 0 ? (
                <SproutEmptyState
                    greeting={greeting ?? emptyStateMessage}
                    introCopy={introCopy}
                    suggestions={suggestions}
                    onSuggestionPress={onSendMessage}
                />
            ) : (
                <YStack>
                    <View
                        onLayout={(event) => {
                            notifyContentHeight(
                                event.nativeEvent.layout.height,
                            );
                        }}
                    >
                        <YStack gap={24} paddingTop="$4">
                            {messages.map((message) => {
                                if (message.role === 'user') {
                                    const bubble = (
                                        <UserMessageBubble
                                            text={formatUserMessageForDisplay(
                                                getMessageText(message),
                                            )}
                                            isError={message.isError ?? false}
                                        />
                                    );

                                    if (message.id === lastUserMessageId) {
                                        return (
                                            <View
                                                key={message.id}
                                                onLayout={createAnchorOnLayout(
                                                    message.id,
                                                )}
                                            >
                                                {bubble}
                                            </View>
                                        );
                                    }

                                    return (
                                        <React.Fragment key={message.id}>
                                            {bubble}
                                        </React.Fragment>
                                    );
                                }

                                const contentParts =
                                    getAssistantDisplayContent(message);

                                return (
                                    <SequentialAssistantContent
                                        key={message.id}
                                        message={message}
                                        contentParts={contentParts}
                                        logoIconTarget={logoIconTarget}
                                        isStreaming={isStreaming}
                                        widgetHandlers={widgetHandlers}
                                        skipTypingAnimation={
                                            !message.isStreaming
                                        } // Restored / completed turns should paint immediately.
                                        suppressLogoIcon={
                                            isTypingIndicatorActive
                                        }
                                        onContentPartRendered={
                                            onContentPartRendered
                                        }
                                    />
                                );
                            })}
                            {isTypingIndicatorActive ? (
                                <TypingIndicator isActive />
                            ) : null}
                        </YStack>
                    </View>
                    <View style={{ height: bottomPadding }} />
                </YStack>
            )}
        </ChatMessageLayout>
    );
};
